import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import KeywordFilter from "./../KeywordFilter/KeywordFilter";
import ReactTooltip from "react-tooltip";
import filterInvest from "../../lib/models/filterInvest.mode";
import FilterIcon from "../../assets/icons/FilterWhite.svg";
import FilterTriangle from "../../assets/icons/FilterTriangle.svg";
import "./InvestFilterDetail.scss";

const InvestFilterDetail = ({
  setNameFilter,
  setNumberPage,
  setValueSelected,
  setPageSize,
  setBusinessLineFilter,
  setInvestmentStateFilter,
  setcampaignStateFilter,
  setPaymentStateFilter,
  setisFilterBy,
  isDisabled,
  isFilterByDisabled
}: any) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [selectedBusinessLine, setSelectedBusinessLine] = useState(
    filterInvest.businessLine.todos
  );
  const [selectedTransactionStates, setSelectedTransactionStates] = useState<
    string[]
  >([]);
  const filterPopupRef = useRef<any>(null);

  const businessLineOptions = [
    {
      value: filterInvest.businessLine.todos,
      label: t("searchInvestDetail.businessLineOp.todos")
    },
    {
      value: filterInvest.businessLine.debt,
      label: t("searchInvestDetail.businessLineOp.deuda")
    },
    {
      value: filterInvest.businessLine.shares,
      label: t("searchInvestDetail.businessLineOp.acciones")
    },
    {
      value: filterInvest.businessLine.convertibleNote,
      label: t("searchInvestDetail.businessLineOp.nota")
    }
  ];

  const transactionStateOptions = [
    {
      value: "pending",
      label: t("searchInvestDetail.investmentStateOp.pending")
    },
    {
      value: filterInvest.investmentState.active,
      label: t("searchInvestDetail.investmentStateOp.active")
    }
  ];

  const paymentStateOptions = [
    {
      value: "onTime",
      label: t("searchInvestDetail.paymentStateOp.onTime")
    },
    {
      value: "pending",
      label: t("searchInvestDetail.paymentStateOp.pending")
    }
  ];

  const moraStateOptions = [
    {
      value: "inMora",
      label: t("searchInvestDetail.moraStateOp.inMora")
    },
    {
      value: "guaranteeExecuted",
      label: t("searchInvestDetail.moraStateOp.guaranteeExecuted")
    }
  ];

  const isActiveInvestments = activeTab === 1;
  const isMoraInvestments = activeTab === 2;
  const isFinishedInvestments = activeTab === 3;
  const isPaymentStateTab = isActiveInvestments || isMoraInvestments;
  const stateSectionHeader = isPaymentStateTab
    ? t("searchInvestDetail.paymentState")
    : t("searchInvestDetail.investmentState");
  const stateSectionOptions = isActiveInvestments
    ? paymentStateOptions
    : isMoraInvestments
    ? moraStateOptions
    : transactionStateOptions;

  const tabs = [
    {
      label: t("searchInvestDetail.tabs.open"),
      tooltip: t("searchInvestDetail.tabsTooltip.open"),
      investmentState: filterInvest.investmentState.defaultValue,
      campaignState: filterInvest.campaignState.active
    },
    {
      label: t("searchInvestDetail.tabs.active"),
      tooltip: t("searchInvestDetail.tabsTooltip.active"),
      investmentState: filterInvest.investmentState.active,
      campaignState: filterInvest.campaignState.activeInvestments
    },
    {
      label: t("searchInvestDetail.tabs.overdue"),
      tooltip: t("searchInvestDetail.tabsTooltip.overdue"),
      investmentState: filterInvest.investmentState.mora,
      campaignState: filterInvest.campaignState.mora
    },
    {
      label: t("searchInvestDetail.tabs.finished"),
      tooltip: t("searchInvestDetail.tabsTooltip.finished"),
      investmentState: filterInvest.investmentState.defaultValue,
      campaignState: filterInvest.campaignState.close
    }
  ];

  const restoreValues = () => {
    setValueSelected(1);
    setNumberPage(1);
    if (setPageSize) {
      setPageSize(10);
    }
  };

  const keywordFilterSearch = (e: any) => {
    setKeyword(e.target.value);
  };

  const onSearchKeyword = () => {
    if (isDisabled) return;
    restoreValues();
    setNameFilter(keyword);
  };

  const onSelectTab = (index: number) => {
    if (isDisabled) return;
    const tab = tabs[index];
    setActiveTab(index);
    restoreValues();
    setBusinessLineFilter(filterInvest.businessLine.defaultValue);
    setInvestmentStateFilter(tab.investmentState);
    setcampaignStateFilter(tab.campaignState);
    setPaymentStateFilter("");
    setSelectedTransactionStates([]);
    setisFilterBy(true);
  };

  const toggleFilterPopup = () => {
    if (isDisabled || isFilterByDisabled) return;
    setShowFilterPopup((prev) => !prev);
  };

  const onSelectBusinessLine = (value: number) => {
    setSelectedBusinessLine(value);
  };

  const onToggleTransactionState = (value: string) => {
    setSelectedTransactionStates((prev) =>
      prev.includes(value) ? [] : [value]
    );
  };

  const isResetDisabled =
    selectedBusinessLine === filterInvest.businessLine.todos &&
    selectedTransactionStates.length === 0;

  const onResetFilterPopup = () => {
    if (isResetDisabled) return;
    setSelectedBusinessLine(filterInvest.businessLine.todos);
    setSelectedTransactionStates([]);
  };

  const onApplyFilterPopup = () => {
    restoreValues();
    setBusinessLineFilter(selectedBusinessLine);
    if (isPaymentStateTab) {
      const currentStateOptions = isActiveInvestments
        ? paymentStateOptions
        : moraStateOptions;
      const isAllOrNone =
        selectedTransactionStates.length === 0 ||
        selectedTransactionStates.length === currentStateOptions.length;
      setPaymentStateFilter(
        isAllOrNone ? "" : selectedTransactionStates.join(",")
      );
    } else {
      setPaymentStateFilter("");
      const hasTransactionStateSelected =
        activeTab === 0 && selectedTransactionStates.length > 0;
      setInvestmentStateFilter(
        hasTransactionStateSelected
          ? selectedTransactionStates[0]
          : tabs[activeTab].investmentState
      );
    }
    setisFilterBy(true);
    setShowFilterPopup(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target)
      ) {
        setShowFilterPopup(false);
      }
    };
    if (showFilterPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterPopup]);

  const renderCheckbox = (checked: boolean) => (
    <span
      className={`investFilter__check ${
        checked ? "investFilter__check--checked" : ""
      }`}
    />
  );

  return (
    <div className="investFilter">
      <div className="investFilter__title">
        <p>{t("searchInvestDetail.title")}</p>
      </div>
      <div className="investFilter__card">
        <div className="investFilter__tabs">
          {tabs.map((tab, index) => (
            <button
              key={tab.label}
              type="button"
              data-testid={`tab-${index}`}
              data-tip={tab.tooltip}
              data-for={`tab-tooltip-${index}`}
              className={`investFilter__tab ${
                activeTab === index ? "active" : ""
              }`}
              onClick={() => onSelectTab(index)}
              disabled={isDisabled}
            >
              {tab.label}
              <ReactTooltip
                id={`tab-tooltip-${index}`}
                effect="solid"
                place="top"
                html={true}
                className="investFilter__tab-tooltip"
              />
            </button>
          ))}
        </div>
        <div className="investFilter__search">
          <div className="investFilter__filterWrapper" ref={filterPopupRef}>
            <button
              type="button"
              className="investFilter__filterBtn"
              disabled={isDisabled || isFilterByDisabled}
              onClick={toggleFilterPopup}
            >
              {t("searchInvestDetail.by")}
              <img
                src={FilterIcon}
                alt=""
                className="investFilter__filterBtn-icon"
              />
            </button>

            {showFilterPopup && (
              <div className="investFilter__popup">
                <img
                  src={FilterTriangle}
                  alt=""
                  className="investFilter__popupArrow"
                />
                <div className="investFilter__popupCard">
                <div className="investFilter__popupTitle">
                  {t("searchInvestDetail.title")}
                </div>

                <div className="investFilter__section">
                  <div className="investFilter__sectionHeader">
                    {t("searchInvestDetail.businessLine")}
                  </div>
                  <div className="investFilter__sectionBody">
                    {businessLineOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="investFilter__option"
                        onClick={() => onSelectBusinessLine(option.value)}
                      >
                        {renderCheckbox(selectedBusinessLine === option.value)}
                        <span className="investFilter__optionLabel">
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {!isFinishedInvestments && (
                <div className="investFilter__section">
                  <div className="investFilter__sectionHeader investFilter__sectionHeader--noTop">
                    {stateSectionHeader}
                  </div>
                  <div className="investFilter__sectionBody">
                    {stateSectionOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="investFilter__option"
                        onClick={() => onToggleTransactionState(option.value)}
                      >
                        {renderCheckbox(
                          selectedTransactionStates.includes(option.value)
                        )}
                        <span className="investFilter__optionLabel">
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                )}

                <div className="investFilter__popupFooter">
                  <button
                    type="button"
                    className="investFilter__popupBtn investFilter__popupBtn--outline"
                    onClick={onResetFilterPopup}
                    disabled={isResetDisabled}
                  >
                    {t("searchInvestDetail.Restore")}
                  </button>
                  <button
                    type="button"
                    className="investFilter__popupBtn investFilter__popupBtn--solid"
                    onClick={onApplyFilterPopup}
                  >
                    {t("searchInvestDetail.Filter")}
                  </button>
                </div>
                </div>
              </div>
            )}
          </div>
          <KeywordFilter
            isDisabled={isDisabled}
            keywordFilterSearch={keywordFilterSearch}
            value={keyword}
            onSearch={onSearchKeyword}
          />
        </div>
      </div>
    </div>
  );
};

export default InvestFilterDetail;
